import type { IHelpGuideSummary } from '@apis/help';

export interface IHelpNode {
  guide: IHelpGuideSummary;
  children: IHelpGuideSummary[];
}

const byOrder = (first: IHelpGuideSummary, second: IHelpGuideSummary) => first.order - second.order;

/** Top-level guides with their children. A child whose parent the caller can't
 *  see is shown at the top level rather than dropped. */
export function buildHelpTree(guides: IHelpGuideSummary[]): IHelpNode[] {
  const slugs = new Set(guides.map((guide) => guide.slug));
  const top = guides.filter((guide) => guide.parentSlug === null || !slugs.has(guide.parentSlug)).sort(byOrder);
  return top.map((guide) => ({ guide, children: guides.filter((child) => child.parentSlug === guide.slug).sort(byOrder) }));
}

/** CSS `order` per top-level slug: parent cards span two columns and sit in the
 *  last row beside the last single card, so the grid ends without a gap. */
export function gridOrder(nodes: IHelpNode[]): Map<string, number> {
  const order = new Map(nodes.map((node) => [node.guide.slug, 0]));
  const parents = nodes.filter((node) => node.children.length > 0);
  if (parents.length === 0) return order;
  for (const parent of parents) order.set(parent.guide.slug, 1);
  const singles = nodes.filter((node) => node.children.length === 0);
  const last = singles[singles.length - 1];
  if (last) order.set(last.guide.slug, 2);
  return order;
}

export function siblingsOf(nodes: IHelpNode[], slug: string): { parent: IHelpGuideSummary | null; siblings: IHelpGuideSummary[]; index: number } {
  const node = nodes.find((candidate) => candidate.children.some((child) => child.slug === slug));
  if (!node) return { parent: null, siblings: [], index: -1 };
  return { parent: node.guide, siblings: node.children, index: node.children.findIndex((child) => child.slug === slug) };
}
